import React, { FC, useEffect, useState } from 'react';
import { EmployedsFilterOptionsList } from './shared/EmployedsFilterOptionsList';
import { EmployedsFilterContainer } from './shared/EmployedsFilterContainer';
import { useDebouncedValue } from '@mantine/hooks';

export interface AdaptiveFilterOption {
  keyword: string;
  title: string;
}

export interface SelectedItem {
  id: string;
  name: string;
}

export interface AdaptiveFilterProps {
  options?: AdaptiveFilterOption[];
  selectedValues: string[] | SelectedItem[];
  onChange: (values: string[] | SelectedItem[]) => void;
  onReset?: () => void;
  onApply?: () => void;
  searchPlaceholder?: string;
  singleSelect?: boolean;
  showSearch?: boolean;
  text?: string | null;
  serverSearch?: boolean;
  onServerSearchChange?: (search: string) => void;
  isFetchingTypes?: boolean;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  storeFullObject?: boolean;
  isLoadingCategorys?: boolean;
}

export const EmployedsAdaptiveFilter: FC<AdaptiveFilterProps> = ({
  options,
  selectedValues,
  onChange,
  searchPlaceholder,
  singleSelect = false,
  showSearch = true,
  text,
  serverSearch,
  onServerSearchChange,
  isFetchingTypes,
  hasNextPage,
  isFetchingMore,
  onLoadMore,
  isLoadingCategorys,
  storeFullObject = false,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput.trim(), 300);

  useEffect(() => {
    if (!serverSearch) return;
    onServerSearchChange?.(debouncedSearch);
  }, [debouncedSearch, onServerSearchChange, serverSearch]);

  const mappedOptions =
    options
      ?.filter(
        (option: AdaptiveFilterOption) =>
          option.title != null && String(option.title).trim() !== '',
      )
      .map((option: AdaptiveFilterOption) => ({
        value: option.keyword,
        label: option.title,
      })) ?? [];

  return (
    <EmployedsFilterContainer>
      <EmployedsFilterOptionsList
        options={mappedOptions}
        selectedValues={selectedValues}
        onChange={onChange}
        searchPlaceholder={searchPlaceholder}
        singleSelect={singleSelect}
        showSearch={showSearch}
        text={text}
        searchValue={searchInput}
        onSearchValueChange={setSearchInput}
        serverSearch={serverSearch}
        isFetchingTypes={isFetchingTypes}
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingMore}
        onLoadMore={onLoadMore}
        storeFullObject={storeFullObject}
        isLoadingCategorys={isLoadingCategorys}
      />
    </EmployedsFilterContainer>
  );
};
